import aa from "search-insights"
import ApiService from "@bodeboca/api"

export default class InsightsUtils {

  private static queryIdCookieName = 'algolia_query_id'
  private static productIdCookieName = 'algolia_product_id'
  public static initInsights() {
    InsightsUtils.initAa()
    InsightsUtils.initFavourites()
  }

  public static initFavourites() {
    const elements = document.getElementsByClassName("bb-favourites-toggle")
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].classList.contains("insights-ready")) {
        continue
      } else {
        elements[i].classList.add("insights-ready")
      }
      let clickedNid = undefined
      // Is product detail page and queryID.
      if (elements[i].attributes["data-nid"] !== undefined) {
        clickedNid = parseInt(elements[i].attributes["data-nid"]["value"])
      } else if (elements[i].parentElement.parentElement.parentElement.attributes["data-nid"] !== undefined) {
        clickedNid = parseInt(elements[i].parentElement.parentElement.parentElement.attributes["data-nid"]["value"])
      }
      if (clickedNid !== undefined) elements[i].addEventListener("click", () => {
        InsightsUtils.addedToFavourites(clickedNid + '')
      })
    }
  }

  public static initAa() {
    const uid = ApiService.getUserId()
    aa("init", {
      appId: ApiService.getAppId(),
      apiKey: ApiService.getApiKey(),
      authenticatedUserToken: uid !== 0 ? uid.toString() : undefined,
      useCookie: true
    })
  }
  public static addedToFavourites(productId: string) {
    InsightsUtils.initAa()
    const event = InsightsUtils.getDefaultEventObject(productId)
    event.eventName = "Product added to favourites"
    aa("convertedObjectIDs", event)
  }

  public static clickedObjectIDsAfterSearch(productId: string): void {
    InsightsUtils.initAa()
    // DOCS: https://www.algolia.com/doc/api-reference/api-methods/clicked-object-ids-after-search/
    const event = InsightsUtils.getDefaultEventObject(productId)
    event.eventName = "Product clicked after search"

    aa("convertedObjectIDsAfterSearch", event)
  }

  public static viewedProductDetailAfterSearch(productId: string, queryId?: string): void {
    InsightsUtils.initAa()
    // DOCS: https://www.algolia.com/doc/api-reference/api-methods/clicked-object-ids-after-search/
    const event = InsightsUtils.getDefaultEventObject(productId, queryId)
    event.eventName = "Product detail viewed after search"

    aa("viewedObjectIDs", event)
  }

  public static viewedProductDetailWithoutSearch(productId: string): void {
    InsightsUtils.initAa()
    // DOCS: https://www.algolia.com/doc/api-reference/api-methods/clicked-object-ids-after-search/
    const event = InsightsUtils.getDefaultEventObject(productId)
    event.eventName = "Product detail viewed without search"

    aa("viewedObjectIDs", event)
  }

  public static addedToCartObjectIDsAfterSearch(productId: string, queryId?: string): void {
    InsightsUtils.initAa()
    // DOCS: https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids-after-search/
    const event = InsightsUtils.getDefaultEventObject(productId, queryId)
    event.eventName = "Added to cart after search (detail)"

    aa("addedToCartObjectIDsAfterSearch", event)
  }

  public static addedToCartObjectIDs(productId: string): void {
    InsightsUtils.initAa()
    // DOCS: https://www.algolia.com/doc/api-reference/api-methods/added-to-cart-object-ids/
    const event = InsightsUtils.getDefaultEventObject(productId)
    event.eventName = "Added to cart without search"

    aa("addedToCartObjectIDs", event)
  }

  public static extractGoogleAnalyticsUserIdFromCookie(gaCookie: string): string | undefined {
    if (gaCookie) {
      // Remove the Google Analytics tracker from the device ID.
      const userIdParts = gaCookie.split(".").slice(-2)
      if (userIdParts.length === 2) {
        return userIdParts.join("_")
      }
    }
    return undefined
  }

  public static getBrowserCookie(cookieName: string): string | undefined {
    // In React Native environments, `document.cookie` doesn't exist.
    if (typeof document !== "object" || typeof document.cookie !== "string") {
      return undefined
    }
    const name = cookieName + "="
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(";")
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === " ") {
        c = c.substring(1)
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length)
      }
    }
    return undefined
  }

  public static getGoogleAnalyticsUserIdFromBrowserCookie(cookieName: string,): string | undefined {
    const browserCookie = InsightsUtils.getBrowserCookie(cookieName)

    if (!browserCookie) {
      return undefined
    }

    return InsightsUtils.extractGoogleAnalyticsUserIdFromCookie(browserCookie)
  }

  private static getDefaultEventObject(productId: string, queryId?: string,): any {
    const productIdString = productId.toString()
    if (queryId === undefined) {
      queryId = InsightsUtils.getQueryId(productIdString)
    }
    const defaultObject = {
      index: ApiService.getDefaultIndex(),
      queryID: queryId,
      objectIDs: [productIdString],
    }
    if (queryId) {
      defaultObject.queryID = queryId
    }
    return defaultObject
  }

  /**
   * Set queryId for interacted productId.
   *
   * @param productId - The interacted productId.
   * @param queryId - The current queryId.
   */
  public static setQueryId(productId: string, queryId: string): void {
    const expires = new Date(Date.now() + 6 * 60 * 60 * 1000).toUTCString() // 6 hours from now

    document.cookie = `${this.queryIdCookieName}=${encodeURIComponent(queryId)}; path=/; expires=${expires}; SameSite=Lax`
    document.cookie = `${this.productIdCookieName}=${encodeURIComponent(productId)}; path=/; expires=${expires}; SameSite=Lax`
  }
  /**
   * Get queryId if product matches.
   *
   * @param productId - The queried productId.
   *
   * @param deleteUnmatched - If true, unset queryId and product
   * @return queryId if productId matches, otherwise undefined.
   */
  public static getQueryId(productId: string, deleteUnmatched: bool = true): string | undefined {
    const matchProductId = document.cookie.match(
      new RegExp('(^|;\\s*)' + this.productIdCookieName + '=([^;]*)')
    )
    if (matchProductId && matchProductId[2] === productId) {
      const match = document.cookie.match(
        new RegExp('(^|;\\s*)' + this.queryIdCookieName + '=([^;]*)')
      )
      return match ? decodeURIComponent(match[2]) : undefined
    }
    else {
      // If firing add to cart event for different
      // product than the one we came from.
      if (deleteUnmatched) {
        InsightsUtils.unsetQueryId()
      }
      return undefined
    }
  }

  /**
   * Unset queryId and productId cookies.
   */
  public static unsetQueryId(): void {
    document.cookie = `${this.queryIdCookieName}=; Max-Age=0; path=/; SameSite=Lax`
    document.cookie = `${this.productIdCookieName}=; Max-Age=0; path=/; SameSite=Lax`
  }
  
  
}
